import React, { useMemo } from 'react'
import { StyleSheet, TouchableOpacity } from 'react-native'
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons'
import { NavigationRoute } from '@/main/config/router-navigation'
import { MenuAccessPermissionsConfigs } from '@/domain/models'
import { Badge, Box, HStack, Text, notificationsState } from '@/presentation/components'
import { useMediaQueries, useTheme } from '@/presentation/hooks'
import { useRecoilValue } from 'recoil'

type PageKey =
  | 'LiberaFinanceiro'
  | 'LiberaOrdensServico'
  | 'LiberaSAC'
  | 'LiberaNotasFiscais'
  | 'LiberaMonitoramento'
  | 'LiberaRastreamento'
  | 'LiberaAceiteDigital'
  | 'LiberaAvisoViagem'
  | 'LiberaMeuContrato'

type ItemMenuContatosProps = {
  page: PageKey | keyof MenuAccessPermissionsConfigs
  navigation: any
}

const colorsPage: Record<PageKey, string> = {
  LiberaFinanceiro: '#ed7809',
  LiberaOrdensServico: '#14a10a',
  LiberaSAC: '#cf0707',
  LiberaNotasFiscais: '#d6ba06',
  LiberaMonitoramento: '#08a8dd',
  LiberaRastreamento: '#0bfc03',
  LiberaAceiteDigital: '#0a41ae',
  LiberaAvisoViagem: '#9b0aae',
  LiberaMeuContrato: '#fc4850'
}

export const ItemMenuContatos: React.FC<ItemMenuContatosProps> = ({ page, navigation }) => {
  const theme = useTheme()
  const { isMobile, width } = useMediaQueries()
  const localNotifications = useRecoilValue(notificationsState)

  const iconPage = useMemo(
    () =>
      ({
        LiberaFinanceiro: (
          <MaterialIcons
            name="attach-money"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaOrdensServico: (
          <MaterialIcons
            name="work-outline"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaSAC: (
          <MaterialIcons
            name="support-agent"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaNotasFiscais: (
          <MaterialCommunityIcons
            name="file-document-outline"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaMonitoramento: (
          <MaterialCommunityIcons
            name="alarm-light-outline"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaRastreamento: (
          <MaterialIcons
            name="track-changes"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaAceiteDigital: (
          <MaterialCommunityIcons
            name="file-document-edit-outline"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaAvisoViagem: (
          <MaterialCommunityIcons
            name="airplane-alert"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        ),
        LiberaMeuContrato: (
          <MaterialCommunityIcons
            name="handshake-outline"
            size={isMobile ? 24 : 36}
            color={theme.colors.white}
          />
        )
      } as const)[page as PageKey],
    [page, isMobile, theme.colors.white]
  )

  const iconButton = (): JSX.Element => (
    <Box style={styles.iconContainer}>
      <Box
        style={{
          ...styles.iconContent,
          backgroundColor: `${colorsPage[page as PageKey]}CC`
        }}
      >
        {iconPage}
      </Box>
    </Box>
  )

  const title = (
    {
      LiberaFinanceiro: 'Financeiro',
      LiberaOrdensServico: isMobile ? 'Ordens' : 'Ordens de Serviço',
      LiberaSAC: 'SAC',
      LiberaNotasFiscais: isMobile ? 'Notas' : 'Notas Fiscais',
      LiberaMonitoramento: 'Monitoramento',
      LiberaRastreamento: 'Rastreamento',
      LiberaAceiteDigital: isMobile ? 'Aceite' : 'Aceite Digital',
      LiberaAvisoViagem: isMobile ? 'Viagem' : 'Aviso de Viagem',
      LiberaMeuContrato: isMobile ? 'Contrato' : 'Meu Contrato'
    } as const
  )[page as PageKey]

  const route = (
    {
      LiberaFinanceiro: NavigationRoute.Financial,
      LiberaOrdensServico: NavigationRoute.ServiceOrders,
      LiberaSAC: NavigationRoute.SAC,
      LiberaNotasFiscais: NavigationRoute.Invoices,
      LiberaMonitoramento: NavigationRoute.Monitoring,
      LiberaRastreamento: NavigationRoute.Track,
      LiberaAceiteDigital: NavigationRoute.DigitalAccepts,
      LiberaAvisoViagem: NavigationRoute.TravelAlert,
      LiberaMeuContrato: NavigationRoute.Contract
    } as const
  )[page as PageKey]

  const containerStyles = {
    ...styles.container,
    ...(!isMobile
      ? {
          maxHeight: 180,
          maxWidth: 180,
          minHeight: 140
        }
      : {
          flex: 0.5,
          minHeight: 120
        }),
    minWidth: isMobile ? (width - 56) / 2 : '30%',
    backgroundColor: colorsPage[page as PageKey]
  }

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      style={containerStyles}
      onPress={() => navigation.navigate(route)}
      testID={`home-button-${title}`}
    >
      <HStack justify="between" fill>
        {iconButton()}
        <Badge
          label={
            localNotifications.filter((notification) => notification.screen === route).length
          }
          style={{ marginRight: 18, paddingHorizontal: 12, height: 32, borderRadius: 50 }}
          color={theme.colors.background}
          labelStyle={{ color: colorsPage[page as PageKey], fontWeight: 'bold' }}
        />
      </HStack>
      <HStack fill items="end">
        <Text
          color={theme.colors.white}
          style={{
            ...styles.title,
            fontSize: isMobile ? 16 : 18
          }}
        >
          {title}
        </Text>
      </HStack>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    paddingVertical: 18,
    borderRadius: 16,
    flexWrap: 'wrap'
  },
  title: {
    fontWeight: '500',
    paddingHorizontal: 18
  },
  iconContainer: {
    borderRadius: 9,
    backgroundColor: '#FFFFFF',
    marginLeft: 18
  },
  iconContent: {
    borderRadius: 8,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1
  }
})
